<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { routePlansApi } from '@/api/route-plans';
import RoutePlanSetup from './route-plan-setup.vue';
import RoutePlanBoard from './route-plan-board.vue';
import RoutePlanApplied from './route-plan-applied.vue';
import RoutePlanLifecycleStepper from './route-plan-lifecycle-stepper.vue';

const POLLING_INTERVAL = 3000;
// Low-frequency background refresh while the board is open, so coordinators editing the same
// plan see each other's granular changes within a few seconds.
const BOARD_REFRESH_INTERVAL = 10000;

const { t } = useI18n();

interface Props {
  routePlanId: number;
}

const props = defineProps<Props>();

const isPolling = ref(false);
const isBoardActive = ref(false);

const { data: routePlan, isPending, refetch } = useQuery({
  queryKey: ['routePlan', props.routePlanId],
  queryFn: async () => {
    const result = await routePlansApi.show(props.routePlanId);
    isPolling.value = result.status === 'processing';
    isBoardActive.value = result.status === 'completed';

    return result;
  },
  refetchInterval: computed(() => {
    if (isPolling.value) return POLLING_INTERVAL;

    return isBoardActive.value ? BOARD_REFRESH_INTERVAL : false;
  }),
  refetchOnWindowFocus: true,
});

const status = computed(() => routePlan.value?.status);
const errorAlert = ref<InstanceType<typeof import('vuetify/components').VAlert> | null>(null);

watch(status, async (newStatus) => {
  await nextTick();
  if (newStatus === 'failed') {
    (errorAlert.value?.$el as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
});

// The board (completed) spans the full width so its left drawer can use the page's left edge
// instead of being inset by the centered column; the other steps stay centered for readability.
const containerClass = computed(() => (
  status.value === 'completed' ? 'v-col-12 px-2' : 'v-col-12 v-col-md-10'
));
</script>

<template>
  <v-container :class="containerClass">
    <div
      v-if="isPending"
      class="d-flex justify-center pa-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <template v-else-if="routePlan">
      <route-plan-lifecycle-stepper
        :status="routePlan.status"
        class="mb-4"
      />

      <route-plan-setup
        v-if="status === 'draft'"
        :route-plan="routePlan"
        @optimized="refetch()"
      />

      <div
        v-else-if="status === 'processing'"
        class="d-flex flex-column align-center pa-8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <p class="mt-4 text-body-1">
          {{ t('routePlan.optimizing') }}
        </p>
      </div>

      <route-plan-board
        v-else-if="status === 'completed'"
        :route-plan="routePlan"
        @applied="refetch()"
        @updated="refetch()"
      />

      <template v-else-if="status === 'failed'">
        <route-plan-setup
          :route-plan="routePlan"
          @optimized="refetch()"
        />
        <v-alert
          ref="errorAlert"
          type="error"
          class="mt-4 error-alert-preline"
        >
          {{ routePlan.errorMessage || t('routePlan.optimizationFailed') }}
        </v-alert>
      </template>

      <route-plan-applied
        v-else-if="status === 'applied'"
        :route-plan="routePlan"
      />
    </template>
  </v-container>
</template>

<style scoped>
.error-alert-preline {
  white-space: pre-line;
}
</style>
